"use client";

import { useRef } from "react";
import { ArrowRight, MessageSquare } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
const symptoms = [{
  title: "Refrigerator Not Cooling",
  desc: "Spoiling groceries, unsafe compartment temperatures, or compressor cooling failure.",
  path: "/RefrigeratorNotCoolingPage"
}, {
  title: "Washer Not Spinning",
  desc: "Soaked laundry loads, drum agitation failures, or cycle drainage imbalances.",
  path: "/WasherNotSpinningPage"
}, {
  title: "Dryer Not Heating",
  desc: "Extended drying cycles running cold, thermal fuse trips, or heating element issues.",
  path: "/DryerNotHeatingPage"
}, {
  title: "Dishwasher Not Draining",
  desc: "Dirty water pooling in tub bottom, filter assembly blockages, or pump failures.",
  path: "/DishwasherNotDrainingPage"
}, {
  title: "Oven Not Heating",
  desc: "Inconsistent baking temperatures, broken igniters, or non-functional baking elements.",
  path: "/OvenNotHeatingPage"
}, {
  title: "Ice Maker Not Working",
  desc: "Halted ice production, hollow cubes, or clogged water inlet valve lines.",
  path: "/IceMakerNotWorkingPage"
}];
export default function Appliancerepairhero() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".ia-eyebrow", {
        y: 12
      });
      gsap.set(".ia-heading", {
        y: 16
      });
      gsap.set(".ia-phone", {
        y: 20,
        scale: 0.96
      });
      gsap.set(".ia-body", {
        y: 16
      });
      gsap.set(".ia-cta", {
        y: 16
      });
      gsap.set(".ia-symptoms", {
        y: 16
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.to(".ia-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.52
      }).to(".ia-heading", {
        opacity: 1,
        y: 0,
        duration: 0.52
      }, "-=0.34").to(".ia-phone", {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.64
      }, "-=0.28").to(".ia-body", {
        opacity: 1,
        y: 0,
        duration: 0.52
      }, "-=0.36").to(".ia-cta", {
        opacity: 1,
        y: 0,
        duration: 0.52
      }, "-=0.34").to(".ia-symptoms", {
        opacity: 1,
        y: 0,
        duration: 0.52
      }, "-=0.34");
      gsap.to(".ia-pulse", {
        scale: 1.6,
        opacity: 0,
        duration: 1.4,
        repeat: -1,
        ease: "power1.out"
      });
      gsap.to(".ia-bg", {
        scale: 1.02,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".ia-eyebrow", ".ia-heading", ".ia-phone", ".ia-body", ".ia-cta", ".ia-symptoms"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="32630" ref={container} id="appliancerepairhero" data-nav="dark" className="bg-background text-foreground relative overflow-hidden min-h-[92svh] flex items-center justify-center px-[clamp(1.25rem,4vw,3rem)] py-[clamp(4rem,8vw,7rem)]">
      <div className="ia-bg absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_tech.webp)]" aria-hidden="true" />
      <div className="absolute inset-0 bg-background/[0.72]" aria-hidden="true" />
      <div className="absolute inset-0" style={{
      background: "radial-gradient(ellipse at center, transparent 40%, hsl(219 45% 12% / 0.55) 100%)"
    }} aria-hidden="true" />

      <svg className="absolute bottom-0 left-[-10%] right-[-10%] w-[120%] pointer-events-none" viewBox="0 0 1400 200" preserveAspectRatio="none" aria-hidden="true" style={{
      opacity: 0.06
    }}>
        <path d="M0,180 L120,120 L200,150 L320,70 L380,100 L460,40 L540,90 L640,30 L720,80 L820,50 L920,110 L1020,60 L1120,130 L1220,90 L1320,140 L1400,110 L1400,200 L0,200 Z" fill="none" stroke="hsl(219 38% 45%)" strokeWidth="1.5" />
        <rect x="470" y="55" width="14" height="10" fill="hsl(219 38% 45%)" />
        <rect x="820" y="55" width="16" height="12" fill="hsl(219 38% 45%)" />
      </svg>

      <div className="relative z-10 w-full max-w-[960px] mx-auto text-center flex flex-col items-center">
        <div className="ia-eyebrow flex items-center justify-center gap-3" style={{
        opacity: 0
      }}>
          <span aria-hidden="true" className="inline-block h-3 w-[1.5px] bg-primary" />
          <span className="relative flex items-center justify-center">
            <span className="relative flex h-[10px] w-[10px] items-center justify-center mr-2">
              <span className="ia-pulse absolute inline-flex h-[10px] w-[10px] rounded-lg bg-primary" aria-hidden="true" />
              <span className="relative inline-flex h-[6px] w-[6px] rounded-lg bg-primary" />
            </span>
            <span className="font-default text-xs font-semibold uppercase text-primary" style={{
            letterSpacing: "0.22em"
          }}>
              {"Atlanta Service Area \u00B7 Metro Neighborhoods"}
            </span>
          </span>
          <span aria-hidden="true" className="inline-block h-3 w-[1.5px] bg-primary" />
        </div>

        <h1 className="ia-heading mt-6 font-serif font-bold text-foreground" style={{
        opacity: 0,
        fontSize: "clamp(2rem, 4vw, 3.5rem)",
        lineHeight: 1.08,
        letterSpacing: "-0.02em"
      }}>
          <span className="block">Appliance Repair in Atlanta, GA</span>
          <span className="block text-primary text-xl sm:text-2xl md:text-3xl mt-2 font-sans font-medium tracking-normal">
            Connect with Independent Local Pros Across Metro Atlanta
          </span>
        </h1>

        <div className="ia-phone mt-6 w-full" style={{
        opacity: 0
      }}>
          <Link to="tel:+14708479076" className="inline-block font-default font-extrabold text-primary hover:text-primary/90 transition-colors" style={{
          fontSize: "clamp(2.5rem, 9vw, 6.5rem)",
          lineHeight: 0.9,
          letterSpacing: "-0.02em",
          textTransform: "uppercase"
        }}>
            {"(470) 847-9076"}
          </Link>
        </div>

        <div className="ia-body mt-6 max-w-[56ch] space-y-4 text-center" style={{
        opacity: 0
      }}>
          <p className="font-default text-muted-foreground text-base sm:text-lg leading-relaxed">
            From Buckhead and Midtown to Decatur and Sandy Springs, major home
            appliances take a heavy beating in Georgia heat and daily use. When your refrigerator stops cooling
            or your washer fails mid-cycle, Handy Heroes connects you with local pros who troubleshoot and service household systems.
          </p>
          <p className="font-default text-muted-foreground text-base sm:text-lg leading-relaxed">
            Submit your issue to request quotes from independent appliance repair specialists in your neighborhood. You can also explore our metro-wide <Link to="/ApplianceRepairPage" className="text-foreground font-medium underline hover:text-primary transition-colors">appliance repair in Atlanta</Link> guide for common mechanical symptoms and maintenance advice.
          </p>
        </div>

        <div className="ia-cta mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full" style={{
        opacity: 0
      }}>
          <Button asChild className="w-full sm:w-auto max-w-[320px] rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 font-default font-semibold uppercase shadow-md" style={{
          letterSpacing: "0.04em"
        }}>
            <Link to="#bookingform">
              {"Find a Pro"}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full sm:w-auto max-w-[320px] rounded-lg bg-transparent border-[1.5px] border-foreground text-foreground hover:bg-foreground hover:text-background h-12 px-7 font-default font-semibold uppercase" style={{
          letterSpacing: "0.04em"
        }}>
            <Link to="/ContactPage">
              <MessageSquare className="mr-1 h-4 w-4" />
              {"Request a Quote"}
            </Link>
          </Button>
        </div>

        <div className="ia-symptoms mt-14 w-full pt-10 border-t border-border/40" style={{
        opacity: 0
      }}>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-3 text-center">
            Appliance Problems We Help With in Atlanta
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-[52ch] mx-auto mb-8 text-center">
            Select your specific symptom below to explore diagnostic guidance and connect with targeted local repair solutions.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
            {symptoms.map((symptom, i) => <Link key={symptom.path} data-index={i} to={symptom.path} className="group block p-5 rounded-lg border border-border/80 bg-card/60 hover:bg-card hover:border-primary/50 transition-all shadow-xs">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                    {symptom.title}
                  </h3>
                  <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                  {symptom.desc}
                </p>
              </Link>)}
          </div>
        </div>
      </div>
    </section>;
}